import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorMessage } from '@/components/ui/error-message';
import { useOrganizationWorkspaces } from '@/hooks/api/useOrganizations';
import { emailSchema } from '@/lib/validationSchemas';

// Workspace selections / workspace-role live in local useState rather
// than react-hook-form — keeps the array reference stable across
// unrelated form-field changes. See PR #1429.
const validationSchema = Yup.object().shape({
  email: emailSchema,
  role: Yup.string().required('Role is required'),
});

type FormData = Yup.InferType<typeof validationSchema>;

const defaultValues: FormData = {
  email: '',
  role: 'EDITOR',
};

interface InviteMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvite: (
    email: string,
    role: string,
    workspaceIds?: string[],
    workspaceRole?: 'CAN_EDIT' | 'CAN_VIEW'
  ) => void;
  loading?: boolean;
  organizationId?: string;
}

export function InviteMemberDialog({
  open,
  onOpenChange,
  onInvite,
  loading = false,
  organizationId,
}: InviteMemberDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>([]);
  const [workspaceRole, setWorkspaceRole] = useState<'CAN_EDIT' | 'CAN_VIEW'>('CAN_EDIT');

  const defaultFilters = useMemo(() => ({ page: 1, limit: 100 }), []);

  // Fetch organization workspaces if organizationId is provided
  const { data: workspacesData, isLoading: workspacesLoading } = useOrganizationWorkspaces(
    organizationId || '',
    defaultFilters,
  );

  const role = watch('role');

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setSelectedWorkspaceIds([]);
      setWorkspaceRole('CAN_EDIT');
    }
  }, [open, reset]);

  const onFormSubmit = (data: FormData) => {
    onInvite(
      data.email,
      data.role,
      selectedWorkspaceIds.length > 0 ? selectedWorkspaceIds : undefined,
      selectedWorkspaceIds.length > 0 ? workspaceRole : undefined,
    );
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset(defaultValues);
      setSelectedWorkspaceIds([]);
      setWorkspaceRole('CAN_EDIT');
    }
    onOpenChange(newOpen);
  };

  const toggleWorkspace = (workspaceId: string) => {
    setSelectedWorkspaceIds((current) =>
      current.includes(workspaceId)
        ? current.filter((id) => id !== workspaceId)
        : [...current, workspaceId],
    );
  };

  const workspaces = workspacesData?.data || [];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogHeader>
            <DialogTitle>Invite Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join this organization. They will receive an email with
              instructions to get started.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="member@example.com"
                {...register('email')}
                hasError={!!errors.email}
                disabled={loading}
              />
              <ErrorMessage message={errors.email?.message} />
            </div>
            <div className="grid gap-2">
              <label htmlFor="role" className="text-sm font-medium">
                Organization Role
              </label>
              <Select value={role} onValueChange={(val) => setValue('role', val)} disabled={loading}>
                <SelectTrigger id="role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                  <SelectItem value="EDITOR">Editor</SelectItem>
                </SelectContent>
              </Select>
              <ErrorMessage message={errors.role?.message} />
            </div>

            {/* Workspace selection section */}
            {organizationId && (
              <>
                <div className="border-t pt-4 mt-2">
                  <label className="text-sm font-medium">
                    Add to Workspaces (Optional)
                  </label>
                  <p className="text-sm text-muted-foreground mb-3">
                    Select workspaces to add this user to immediately.
                  </p>

                  {workspacesLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                  ) : workspaces.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">
                      No workspaces available in this organization.
                    </p>
                  ) : (
                    <div className="max-h-[150px] overflow-y-auto border rounded-md">
                      {workspaces.map((workspace) => {
                        const checked = selectedWorkspaceIds.includes(workspace._id);
                        return (
                          <div
                            key={workspace._id}
                            role="checkbox"
                            aria-checked={checked}
                            tabIndex={0}
                            className={`p-3 cursor-pointer hover:bg-muted border-b last:border-b-0 flex items-center gap-3 ${
                              checked ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                            }`}
                            onClick={() => toggleWorkspace(workspace._id)}
                            onKeyDown={(e) => {
                              if (e.key === ' ' || e.key === 'Enter') {
                                e.preventDefault();
                                toggleWorkspace(workspace._id);
                              }
                            }}
                          >
                            {/* Native checkbox — Radix Checkbox inside a
                                Dialog caused a Presence-ref loop
                                ("Maximum update depth exceeded"). A plain
                                input avoids the nested Radix Presence
                                interaction. Styled to match Radix sizing. */}
                            <input
                              type="checkbox"
                              checked={checked}
                              readOnly
                              tabIndex={-1}
                              className="pointer-events-none h-4 w-4 rounded-sm border border-primary accent-primary"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-sm">{workspace.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {workspace.memberCount || 0} members
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {selectedWorkspaceIds.length > 0 && (
                  <div className="grid gap-2">
                    <label className="text-sm font-medium">
                      Workspace Role
                    </label>
                    <Select
                      value={workspaceRole}
                      onValueChange={(value: 'CAN_EDIT' | 'CAN_VIEW') => setWorkspaceRole(value)}
                      disabled={loading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select workspace role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CAN_EDIT">Can Edit</SelectItem>
                        <SelectItem value="CAN_VIEW">Can View</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Selected {selectedWorkspaceIds.length} workspace
                      {selectedWorkspaceIds.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
